import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { BUSINESS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleMark = () => (
  <span data-testid="reviews-google-mark" className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white border border-border shadow-sm font-sans font-semibold text-xl">
    <span className="text-[#4285F4]">G</span>
  </span>
);

const Stars = ({ value = 0, size = 15 }) => (
  <span className="flex gap-1" aria-label={`Rated ${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={size}
        strokeWidth={1.2}
        className={i <= Math.round(value) ? "text-gold" : "text-gold/25"}
        fill={i <= Math.round(value) ? "currentColor" : "none"}
      />
    ))}
  </span>
);

export const Reviews = () => {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/google-reviews`)
      .then((res) => {
        if (mounted && res.data.connected && res.data.data?.reviews?.length) setLive(res.data.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section id="reviews" data-testid="reviews-section" className="py-20 md:py-32 bg-ivory scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <SectionHeading
          chapter="07"
          overline="Google Reviews"
          title="What Our Customers Say"
          center
        />

        {live ? (
          <div data-testid="reviews-live" className="-mt-4 md:-mt-8">
            <Reveal className="text-center mb-12">
              <div className="inline-flex flex-col items-center gap-4">
                <GoogleMark />
                <div className="flex items-center gap-4">
                  <span data-testid="reviews-rating-value" className="font-serif text-5xl md:text-6xl text-royal-deep leading-none">
                    {live.rating?.toFixed(1)}
                  </span>
                  <div className="text-left">
                    <Stars value={live.rating} size={17} />
                    <p className="mt-1.5 text-xs tracking-[0.15em] uppercase text-charcoal/50">
                      Based on {live.reviewCount} Google reviews
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {live.reviews.map((r, i) => (
                <Reveal key={i} delay={0.06 * i} className="h-full">
                  <article data-testid={`review-card-${i}`} className="h-full border border-gold/30 bg-white/70 rounded-sm p-7 flex flex-col">
                    <Stars value={r.rating} />
                    <p className="mt-4 text-sm font-light text-charcoal/75 leading-relaxed flex-1 line-clamp-6">
                      {r.text || "Rated on Google"}
                    </p>
                    <div className="mt-5 pt-4 border-t border-gold/20 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        {r.authorPhotoUri ? (
                          <img src={r.authorPhotoUri} alt="" loading="lazy" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <span className="w-8 h-8 rounded-full bg-royal text-ivory flex items-center justify-center font-serif text-sm shrink-0">
                            {(r.authorName || "G")[0]}
                          </span>
                        )}
                        <div className="min-w-0">
                          <p className="font-serif text-base text-royal-deep truncate">{r.authorName}</p>
                          {r.publishTime && (
                            <p className="text-[11px] text-charcoal/45">
                              {new Date(r.publishTime).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}
                            </p>
                          )}
                        </div>
                      </div>
                      {r.googleMapsUri && (
                        <a
                          href={r.googleMapsUri}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="View this review on Google"
                          className="text-charcoal/40 hover:text-gold-muted transition-colors duration-300 shrink-0"
                        >
                          <ExternalLink size={14} strokeWidth={1.5} />
                        </a>
                      )}
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-10 text-center">
              <p className="text-xs text-charcoal/40 font-light mb-6">
                A selection of recent Google reviews, shown as published on Google.
              </p>
              <a
                data-testid="reviews-google-button"
                href={BUSINESS.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-royal text-ivory text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-royal-light transition-colors duration-300"
              >
                View Our Google Reviews <ExternalLink size={14} strokeWidth={1.5} />
              </a>
            </Reveal>
          </div>
        ) : (
          <Reveal className="max-w-2xl mx-auto -mt-4 md:-mt-8">
            <div data-testid="reviews-placeholder-card" className="relative border border-gold/30 bg-white/60 rounded-sm px-8 py-12 md:py-14 text-center">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <GoogleMark />
              </div>
              <div className="flex justify-center gap-1.5 mt-3" aria-label="Star ratings will appear here once connected to live Google reviews">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={20} strokeWidth={1.2} className="text-gold/40" />
                ))}
              </div>
              <p className="mt-6 font-serif text-2xl md:text-3xl text-royal-deep leading-snug">
                Real words from real families
              </p>
              <p className="mt-4 text-sm md:text-base font-light text-charcoal/60 leading-relaxed max-w-md mx-auto">
                We publish only genuine customer feedback. Our live Google Reviews will appear here shortly — until then, read what customers say about us directly on Google.
              </p>
              <a
                data-testid="reviews-google-button"
                href={BUSINESS.reviewsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2.5 px-8 py-3.5 bg-royal text-ivory text-sm tracking-[0.12em] uppercase font-medium rounded-sm hover:bg-royal-light transition-colors duration-300"
              >
                View Our Google Reviews <ExternalLink size={14} strokeWidth={1.5} />
              </a>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};
